import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Check, Play, FileText, Video, Presentation, Clock, Mail, ChevronDown } from 'lucide-react';

const packages = [
  {
    name: 'Spark',
    price: '$249',
    description: '1 lesson + slides + worksheet + 60–90s clip',
    features: ['Single lesson pack', 'Student slides', 'Worksheet + key', 'Short captioned video (60-90s)'],
    popular: false
  },
  {
    name: 'Unit Studio',
    price: '$990',
    description: '5–8 lessons, 3 clips, teacher guide, one revision',
    features: ['5-8 connected lessons', '3 student video clips', 'Complete teacher guide', 'One revision round included'],
    popular: true
  },
  {
    name: 'Campus Build',
    price: 'from $2,800',
    description: 'Full sequence, captions, brand-matched assets',
    features: ['Full unit sequence', 'All videos captioned', 'Brand-matched design', 'Custom asset package'],
    popular: false
  }
];

const addons = [
  { name: 'Extra student video', price: '$175' },
  { name: 'Teacher walkthrough', price: '$125' },
  { name: 'Spanish translation', price: '+20%' },
  { name: 'Rush delivery', price: '+30%' }
];

const deliverables = [
  { icon: FileText, title: 'Teacher Guide', desc: 'Pacing, checks for understanding, one stretch / one support path' },
  { icon: Presentation, title: 'Student Pack', desc: 'Slides, worksheet, exit ticket, key' },
  { icon: Video, title: 'Captioned Video', desc: 'Student or teacher cut, fully captioned' },
  { icon: Clock, title: 'File Handoff', desc: 'Google / PPTX / PDF / MP4 + .srt' }
];

const processSteps = [
  { num: '1', title: 'Brief', desc: 'Fill out the form below' },
  { num: '2', title: 'Sample', desc: 'Sample page of lesson 1 before the rest is billed' },
  { num: '3', title: 'Full Pack', desc: 'Complete delivery + one revision' },
  { num: '4', title: 'Teach', desc: 'You teach it Monday' }
];

export default function EducatorsPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    gradeBand: '',
    subject: '',
    standardCode: '',
    minutes: '',
    deliverables: [],
    deadline: '',
    package: '',
    struggles: '',
    tone: '',
    lms: '',
    materialsLink: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleCheckbox = (value) => {
    setFormData(prev => ({
      ...prev,
      deliverables: prev.deliverables.includes(value)
        ? prev.deliverables.filter(d => d !== value)
        : [...prev.deliverables, value]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In production, this would send to backend
    const mailtoLink = `mailto:shimon@mgn1point6.com?subject=Lesson Brief: ${formData.subject} - ${formData.gradeBand}&body=${encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\nRole: ${formData.role}\n\nGrade Band: ${formData.gradeBand}\nSubject: ${formData.subject}\nStandard Code: ${formData.standardCode || 'Not provided'}\nMinutes Available: ${formData.minutes}\n\nDeliverables: ${formData.deliverables.join(', ')}\nDeadline: ${formData.deadline}\nPackage: ${formData.package}\n\nStudent Struggles: ${formData.struggles || 'Not provided'}\nTone: ${formData.tone || 'Not specified'}\nLMS: ${formData.lms || 'Not specified'}\nMaterials Link: ${formData.materialsLink || 'Not provided'}`
    )}`;
    window.location.href = mailtoLink;
    setFormSubmitted(true);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F6F1E8' }}>
      <Helmet>
        <title>Clear Lesson Studio | Custom Educational Content by Shimon Weiner</title>
        <meta name="description" content="Ready-to-teach materials — written, designed, and cut like a real lesson. Custom explainer videos, lesson packs, and short videos for K-12 educators." />
        <meta property="og:title" content="Clear Lesson Studio | Custom Educational Content" />
        <meta property="og:description" content="Ready-to-teach materials — written, designed, and cut like a real lesson, not a template dump." />
        <meta property="og:image" content="https://img.youtube.com/vi/bIWf5YWGp9w/maxresdefault.jpg" />
        <meta property="og:url" content="https://mgn1point6.com/educators" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Clear Lesson Studio | Custom Educational Content" />
        <meta name="twitter:description" content="Ready-to-teach materials — written, designed, and cut like a real lesson, not a template dump." />
        <meta name="twitter:image" content="https://img.youtube.com/vi/bIWf5YWGp9w/maxresdefault.jpg" />
      </Helmet>
      {/* Header */}
      <header className="sticky top-0 z-50 border-b" style={{ backgroundColor: '#F6F1E8', borderColor: '#0F5C5B20' }}>
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <a href="/" className="text-sm font-medium" style={{ color: '#0F5C5B' }}>
            ← Back to MGN1.6
          </a>
          <span className="text-sm" style={{ color: '#666' }}>Clear Lesson Studio</span>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24 px-5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6" style={{ color: '#0F5C5B' }}>
              Ready-to-teach materials — written, designed, and cut like a real lesson, not a template dump.
            </h1>
            <p className="text-lg md:text-xl mb-8" style={{ color: '#444' }}>
              You send the standard, grade, and time block. You get a lesson pack and a short explainer students can actually follow.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="#brief" 
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-semibold text-white transition-all hover:opacity-90"
                style={{ backgroundColor: '#E8A317' }}
              >
                <Mail className="w-5 h-5" />
                Send a Brief
              </a>
              <a 
                href="#sample" 
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-semibold border-2 transition-all hover:bg-white/50"
                style={{ borderColor: '#0F5C5B', color: '#0F5C5B' }}
              >
                <Play className="w-5 h-5" />
                Watch How I Explain a Hard Idea
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Proof Strip */}
      <section className="py-6 border-y" style={{ backgroundColor: '#0F5C5B', borderColor: '#0F5C5B' }}>
        <div className="max-w-6xl mx-auto px-5">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-white/90">
            <span>BA, Communication & Media Technology</span>
            <span className="hidden sm:inline">·</span>
            <span>Video + writing workflow</span>
            <span className="hidden sm:inline">·</span>
            <span>SOP-level documentation</span>
            <span className="hidden sm:inline">·</span>
            <span>Bend, OR</span>
          </div>
        </div>
      </section>

      {/* Sample Video */}
      <section id="sample" className="py-16 md:py-24 px-5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center" style={{ color: '#0F5C5B' }}>
            Sample of Explanation Style
          </h2>
          <div className="relative rounded-xl overflow-hidden bg-black aspect-video shadow-xl">
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube.com/embed/MZx93g8O_sQ?rel=0&modestbranding=1"
              title="Sample of explanation style"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
          <p className="text-center mt-4 text-sm" style={{ color: '#666' }}>
            Sample of explanation style — not a K–12 lesson. Classroom samples replace this as I produce them.
          </p>
        </div>
      </section>

      {/* Featured Sample Lesson - Long Division Rap */}
      <section className="py-16 md:py-24 px-5" style={{ backgroundColor: '#0F5C5B' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs tracking-[0.3em] uppercase text-white/70 mb-2">Sample Lesson</p>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
              Long Division Hip-Hop
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              A complete educational rap that teaches long division step-by-step using Minecraft/Roblox themes. 
              Problem: 154 ÷ 7 = 22
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Video */}
            <div>
              <div className="relative rounded-xl overflow-hidden bg-black aspect-video shadow-2xl">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/bIWf5YWGp9w?rel=0&modestbranding=1"
                  title="Long Division Hip-Hop Sample"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <p className="text-center mt-3 text-xs text-white/60 italic">
                ⚠️ Images for context only — does not reflect the accuracy of final product
              </p>
            </div>

            {/* Lyrics/Script Preview */}
            <div className="rounded-xl p-6 max-h-[400px] overflow-y-auto" style={{ backgroundColor: '#F6F1E8' }}>
              <h3 className="font-bold mb-4" style={{ color: '#0F5C5B' }}>Script Preview</h3>
              <div className="space-y-4 text-sm" style={{ color: '#444' }}>
                <div>
                  <p className="font-semibold" style={{ color: '#0F5C5B' }}>Intro</p>
                  <p className="italic">Yo, class in session, numbers ready to ride,<br/>We're dividing one-five-four — let me be your guide.</p>
                </div>
                <div>
                  <p className="font-semibold" style={{ color: '#0F5C5B' }}>Verse 1 — Set the Stage</p>
                  <p className="italic">Put 7 on the left, 154 on the right,<br/>Drop the bracket like a build — everything in sight.</p>
                </div>
                <div>
                  <p className="font-semibold" style={{ color: '#0F5C5B' }}>Verse 2 — Divide</p>
                  <p className="italic">Seven into 1? Yo, that's zero times clean,<br/>Too small to battle mobs — you know what I mean.</p>
                </div>
                <div>
                  <p className="font-semibold" style={{ color: '#0F5C5B' }}>Verse 3 — Multiply</p>
                  <p className="italic">Now multiply it out: 7 × 0 = 0,<br/>Drop it under the 1, keep the rhythm in flow.</p>
                </div>
                <div>
                  <p className="font-semibold" style={{ color: '#0F5C5B' }}>Verse 4 — Subtract</p>
                  <p className="italic">Subtract that 0 from 1, the remainder is 1,<br/>Like mining one block — yo, the job's just begun.</p>
                </div>
                <div className="pt-2 border-t" style={{ borderColor: '#0F5C5B30' }}>
                  <p className="text-xs" style={{ color: '#666' }}>+ 7 more verses walking through each step...</p>
                </div>
                <div>
                  <p className="font-semibold" style={{ color: '#0F5C5B' }}>Outro</p>
                  <p className="italic">Long division on the track, now you know the flow,<br/>From the first block drop to the final answer glow.<br/>Math ain't scary — it's a game you can master,<br/>With rhythm, rhyme, and logic — learning faster.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <p className="text-white/70 text-sm mb-4">
              This is the kind of content I create: engaging, step-by-step lessons that students actually want to watch.
            </p>
            <a 
              href="#brief" 
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-semibold text-[#0F5C5B] transition-all hover:opacity-90"
              style={{ backgroundColor: '#E8A317' }}
            >
              <Mail className="w-5 h-5" />
              Request Something Like This
            </a>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16 md:py-24 px-5" style={{ backgroundColor: 'white' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center" style={{ color: '#0F5C5B' }}>
            What You Get
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {deliverables.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-xl border"
                style={{ backgroundColor: '#F6F1E8', borderColor: '#0F5C5B20' }}
              >
                <item.icon className="w-10 h-10 mb-4" style={{ color: '#0F5C5B' }} />
                <h3 className="font-bold text-lg mb-2" style={{ color: '#0F5C5B' }}>{item.title}</h3>
                <p className="text-sm" style={{ color: '#666' }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 px-5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center" style={{ color: '#0F5C5B' }}>
            How It Works
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 text-white"
                  style={{ backgroundColor: '#0F5C5B' }}
                >
                  {step.num}
                </div>
                <h3 className="font-bold mb-2" style={{ color: '#0F5C5B' }}>{step.title}</h3>
                <p className="text-sm" style={{ color: '#666' }}>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 md:py-24 px-5" style={{ backgroundColor: 'white' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-center" style={{ color: '#0F5C5B' }}>
            Packages
          </h2>
          <p className="text-center mb-12" style={{ color: '#666' }}>
            Honest pricing for custom work. No hidden fees.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {packages.map((pkg, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`p-6 rounded-xl border-2 relative ${pkg.popular ? 'border-[#E8A317]' : ''}`}
                style={{ 
                  backgroundColor: '#F6F1E8', 
                  borderColor: pkg.popular ? '#E8A317' : '#0F5C5B20' 
                }}
              >
                {pkg.popular && (
                  <span 
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-semibold text-white"
                    style={{ backgroundColor: '#E8A317' }}
                  >
                    Most Popular
                  </span>
                )}
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#0F5C5B' }}>{pkg.name}</h3>
                <p className="text-3xl font-black mb-2" style={{ color: '#0F5C5B' }}>{pkg.price}</p>
                <p className="text-sm mb-6" style={{ color: '#666' }}>{pkg.description}</p>
                <ul className="space-y-3">
                  {pkg.features.map((feature, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-sm">
                      <Check className="w-5 h-5 shrink-0" style={{ color: '#0F5C5B' }} />
                      <span style={{ color: '#444' }}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Add-ons */}
          <div className="max-w-2xl mx-auto">
            <h3 className="font-bold mb-4 text-center" style={{ color: '#0F5C5B' }}>Add-ons</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {addons.map((addon, i) => (
                <div key={i} className="text-center p-4 rounded-lg" style={{ backgroundColor: '#F6F1E8' }}>
                  <p className="text-sm font-medium" style={{ color: '#0F5C5B' }}>{addon.name}</p>
                  <p className="font-bold" style={{ color: '#E8A317' }}>{addon.price}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Brief Form */}
      <section id="brief" className="py-16 md:py-24 px-5">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-center" style={{ color: '#0F5C5B' }}>
            Send a Brief
          </h2>
          <p className="text-center mb-8" style={{ color: '#666' }}>
            We reply within 2 business days.
          </p>

          {formSubmitted ? (
            <div className="p-8 rounded-xl text-center" style={{ backgroundColor: 'white' }}>
              <Check className="w-16 h-16 mx-auto mb-4" style={{ color: '#0F5C5B' }} />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#0F5C5B' }}>Brief Received</h3>
              <p style={{ color: '#666' }}>Check your email client to complete sending. I'll reply within 2 business days.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 p-8 rounded-xl" style={{ backgroundColor: 'white' }}>
              {/* Required Fields */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F5C5B' }}>Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#0F5C5B30', backgroundColor: '#F6F1E8' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F5C5B' }}>Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#0F5C5B30', backgroundColor: '#F6F1E8' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#0F5C5B' }}>Role *</label>
                <select
                  required
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                  style={{ borderColor: '#0F5C5B30', backgroundColor: '#F6F1E8' }}
                >
                  <option value="">Select role...</option>
                  <option value="teacher">Teacher</option>
                  <option value="tutor">Tutor</option>
                  <option value="school">School/District</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F5C5B' }}>Grade Band *</label>
                  <select
                    required
                    value={formData.gradeBand}
                    onChange={(e) => setFormData({...formData, gradeBand: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#0F5C5B30', backgroundColor: '#F6F1E8' }}
                  >
                    <option value="">Select grade...</option>
                    <option value="K-2">K-2</option>
                    <option value="3-5">3-5</option>
                    <option value="6-8">6-8</option>
                    <option value="9-12">9-12</option>
                    <option value="Higher Ed">Higher Ed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F5C5B' }}>Subject *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., Biology, US History"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#0F5C5B30', backgroundColor: '#F6F1E8' }}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F5C5B' }}>Standard Code (optional)</label>
                  <input
                    type="text"
                    placeholder="e.g., NGSS 5-LS1-1"
                    value={formData.standardCode}
                    onChange={(e) => setFormData({...formData, standardCode: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#0F5C5B30', backgroundColor: '#F6F1E8' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F5C5B' }}>Minutes Available *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., 45 min class period"
                    value={formData.minutes}
                    onChange={(e) => setFormData({...formData, minutes: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#0F5C5B30', backgroundColor: '#F6F1E8' }}
                  />
                </div>
              </div>

              {/* Deliverables Checkboxes */}
              <div>
                <label className="block text-sm font-medium mb-3" style={{ color: '#0F5C5B' }}>Deliverables Wanted *</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {['Lesson guide', 'Slides', 'Worksheet', 'Student video', 'Teacher video'].map((item) => (
                    <label key={item} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.deliverables.includes(item)}
                        onChange={() => handleCheckbox(item)}
                        className="w-5 h-5 rounded"
                        style={{ accentColor: '#0F5C5B' }}
                      />
                      <span className="text-sm" style={{ color: '#444' }}>{item}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F5C5B' }}>Deadline *</label>
                  <input
                    type="date"
                    required
                    value={formData.deadline}
                    onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#0F5C5B30', backgroundColor: '#F6F1E8' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F5C5B' }}>Package *</label>
                  <select
                    required
                    value={formData.package}
                    onChange={(e) => setFormData({...formData, package: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#0F5C5B30', backgroundColor: '#F6F1E8' }}
                  >
                    <option value="">Select package...</option>
                    <option value="Spark ($249)">Spark — $249</option>
                    <option value="Unit Studio ($990)">Unit Studio — $990</option>
                    <option value="Campus Build (from $2,800)">Campus Build — from $2,800</option>
                    <option value="Not sure">Not sure yet</option>
                  </select>
                </div>
              </div>

              {/* Optional Fields */}
              <div className="pt-4 border-t" style={{ borderColor: '#0F5C5B20' }}>
                <p className="text-sm font-medium mb-4" style={{ color: '#0F5C5B' }}>Optional Details</p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm mb-2" style={{ color: '#666' }}>What do students already struggle with?</label>
                    <textarea
                      rows={3}
                      value={formData.struggles}
                      onChange={(e) => setFormData({...formData, struggles: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                      style={{ borderColor: '#0F5C5B30', backgroundColor: '#F6F1E8' }}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm mb-2" style={{ color: '#666' }}>Tone preference</label>
                      <select
                        value={formData.tone}
                        onChange={(e) => setFormData({...formData, tone: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                        style={{ borderColor: '#0F5C5B30', backgroundColor: '#F6F1E8' }}
                      >
                        <option value="">No preference</option>
                        <option value="plain">Plain</option>
                        <option value="warm">Warm</option>
                        <option value="rigorous">Rigorous</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm mb-2" style={{ color: '#666' }}>LMS</label>
                      <select
                        value={formData.lms}
                        onChange={(e) => setFormData({...formData, lms: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                        style={{ borderColor: '#0F5C5B30', backgroundColor: '#F6F1E8' }}
                      >
                        <option value="">None / Not sure</option>
                        <option value="Google Classroom">Google Classroom</option>
                        <option value="Canvas">Canvas</option>
                        <option value="Schoology">Schoology</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm mb-2" style={{ color: '#666' }}>Link to current materials (optional)</label>
                    <input
                      type="url"
                      placeholder="https://..."
                      value={formData.materialsLink}
                      onChange={(e) => setFormData({...formData, materialsLink: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                      style={{ borderColor: '#0F5C5B30', backgroundColor: '#F6F1E8' }}
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="w-full py-4 rounded-lg font-semibold text-white transition-all hover:opacity-90"
                style={{ backgroundColor: '#E8A317' }}
              >
                Submit Brief
              </button>
            </form>
          )}
        </div>
      </section>

      {/* About */}
      <section className="py-16 md:py-24 px-5" style={{ backgroundColor: 'white' }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#0F5C5B' }}>
            About
          </h2>
          <p className="text-lg leading-relaxed" style={{ color: '#444' }}>
            Shimon Weiner writes and produces educational media in Bend. Training is in communication and media technology. Production habit comes from years of shipping files on deadline — including regulated manufacturing documentation, where a sloppy record is not an option. Classroom work uses that same standard: clear language, complete files, nothing missing for Monday.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-5 border-t" style={{ borderColor: '#0F5C5B20' }}>
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm" style={{ color: '#666' }}>
            Built by Shimon Weiner · <a href="/" className="underline" style={{ color: '#0F5C5B' }}>Grad P Fawkes</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
